<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import OrderReturn from "@/services/westore/entities/orderReturn";
import moment from "moment";
import Swal from "sweetalert2";

export default {
  page: {
    title: "Return Details",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  components: {
    Layout,
    PageHeader,
  },
  props: {
    rowData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      title: "Return Details",
      itemDetails: null,
      loaded: true,
    };
  },
  created() {
    this.itemDetails = this.rowData;
    if (!this.rowData) {
      this.fetchDetails(this.$route.params.id);
    }
  },
  methods: {
    async fetchDetails(id) {
      this.loaded = false;
      await OrderReturn.getOne(id)
        .then((res) => {
          this.itemDetails = res.data;
        })
        .finally(() => {
          this.loaded = true;
        });
    },
    convertDate: function (timestampValue) {
      return moment.unix(timestampValue).format("DD/MM/YYYY");
    },
    cancelOrderReturn() {
      Swal.fire({
        title: "Are you sure?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#34c38f",
        cancelButtonColor: "#f46a6a",
        confirmButtonText: "Yes, Cancel it!",
      }).then((result) => {
        if (result.value) {
          OrderReturn.cancel(this.itemDetails.id).then((res) => {
            this.itemDetails = res.data;
            this.successMessage = "Return has been cancelled successfully";
            this.isMessage = true;
          });
        }
      });
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" class="d-print-none" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body" v-if="loaded && itemDetails !== null">
            <h4 class="card-title mb-4 p-2">
              <span
                >{{ $t("Return") }}
                <code class="pink--text"
                  >#{{ this.itemDetails.order_return_number }}</code
                ></span
              >
              <div class="mb-4 float-end hide-from-screen">
                <img
                  src="@/assets/images/logo-dark.png"
                  alt="logo"
                  height="20"
                />
              </div>
            </h4>
            <!-- Details -->
            <div class="table-responsive">
              <table class="table mb-0">
                <tbody>
                  <tr>
                    <th scope="row">{{ $t("CREATED AT") }}</th>
                    <td>{{ convertDate(itemDetails.created) }}</td>
                  </tr>
                  <tr>
                    <th scope="row">{{ $t("Status") }}</th>
                    <td>
                      <span
                        class="badge badge-soft-warning"
                        v-if="itemDetails.status === 'Initiated'"
                        >{{ itemDetails.status }}</span
                      >
                      <span
                        class="badge badge-soft-info"
                        v-if="itemDetails.status === 'Returned'"
                        >{{ itemDetails.status }}</span
                      >
                      <span
                        class="badge badge-soft-success"
                        v-if="itemDetails.status === 'Accepted'"
                        >{{ itemDetails.status }}</span
                      >
                      <span
                        class="badge badge-soft-danger"
                        v-if="itemDetails.status === 'Rejected'"
                        >{{ itemDetails.status }}</span
                      >
                      <span
                        class="badge badge-soft-danger"
                        v-if="itemDetails.status === 'Cancelled'"
                        >{{ itemDetails.status }}</span
                      >
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">{{ $t("ORDER NUMBER") }}</th>
                    <td>
                      <code class="pink--text"
                        >#{{ itemDetails.order_number }}</code
                      >
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">{{ $t("CUSTOMER NAME") }}</th>
                    <td>{{ itemDetails.recipient.name }}</td>
                  </tr>
                  <tr>
                    <th scope="row">{{ $t("CUSTOMER EMAIL") }}</th>
                    <td>{{ itemDetails.recipient.email }}</td>
                  </tr>
                  <tr>
                    <th scope="row">{{ $t("CUSTOMER PHONE") }}</th>
                    <td>{{ itemDetails.recipient.phone }}</td>
                  </tr>
                  <tr>
                    <th scope="row">{{ $t("CUSTOMER CITY/AREA") }}</th>
                    <td>{{ itemDetails.city.name }}</td>
                  </tr>
                  <tr>
                    <th scope="row">{{ $t("CUSTOMER ADDRESS") }}</th>
                    <td>{{ itemDetails.recipient.default_address.details }}</td>
                  </tr>
                  <tr>
                    <th scope="row">{{ $t("EXPECTED RETURN DATE") }}</th>
                    <td>{{ itemDetails.expected_delivery_date }}</td>
                  </tr>
                  <tr>
                    <th scope="row">{{ $t("Order Items") }}</th>
                    <td>
                      <!-- Cancel Order Return -->
                      <div
                        class="col-xl d-print-none"
                        v-if="itemDetails.status === 'Initiated'"
                      >
                        <div class="d-flex">
                          <button
                            type="button"
                            class="btn btn-danger mr-1"
                            @click="cancelOrderReturn()"
                          >
                            {{$t('Cancel')}}
                          </button>
                        </div>
                      </div>
                      <table class="table table-striped mb-0 border">
                        <thead>
                          <tr>
                            <th scope="col">{{ $t("SKU") }}</th>
                            <th scope="col">{{ $t("PRODUCT NAME") }}</th>
                            <th scope="col">{{ $t("UNIT PRICE (AED)") }}</th>
                            <th scope="col">{{ $t("QUANTITY") }}</th>
                            <th scope="col">{{ $t("Total Price") }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(item, index) in itemDetails.products"
                            :key="index"
                          >
                            <td>{{ item.sku }}</td>
                            <td>{{ item.name }}</td>
                            <td>{{ item.price }}</td>
                            <td>{{ item.quantity }}</td>
                            <td>{{ item.amount }}</td>
                          </tr>
                          <tr>
                            <th colspan="4">{{ $t("Total") }}</th>
                            <td>
                              <span class="badge badge-soft-info">{{
                                itemDetails.total
                              }}</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <!-- Back and get support buttons -->
           <div class="d-flex align-items-center mt-4 d-print-none">
              <router-link :to="{ name: 'support-tickets-list'}">
                <b-btn class="btn btn-info"> <i class="bx bx-arrow-back"></i> {{$t('Back')}}</b-btn>
              </router-link>
              <div class="">
                <a href="javascript:window.print()" :class="isRtl ? 'btn btn-success mr-2':'btn btn-success ml-2'"><i class="fa fa-print text-white"></i></a>
                <router-link :to="{ name: 'get-support'}">
                  <b-btn :class="isRtl ? 'btn btn-warning float-end mr-2':'btn btn-warning float-end ml-2'"><i class="bx bx-support"></i> {{$t('Get Support')}}</b-btn>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
<style>
@media screen {
  .hide-from-screen {
    display: none;
  }
}
</style>